/**
 * Domain error codes and the user-readable copy they map to. SPEC.md §35.
 *
 * The wire only ever carries a code plus already-sanitised copy. Stack traces,
 * SQL text, token material and internal IDs must never reach the UI.
 */

export const ERROR_CODES = [
  'VALIDATION_FAILED',
  'SESSION_NOT_FOUND',
  'SESSION_LOCKED',
  'SESSION_ENDED',
  'SESSION_FULL',
  'ROUND_NOT_FOUND',
  'ROUND_NOT_ACTIVE',
  'ROUND_ALREADY_ACTIVE',
  'ROUND_ALREADY_COMPLETED',
  'ROUND_ALREADY_VOIDED',
  'NOT_ENOUGH_PLAYERS',
  'PLAYER_NOT_FOUND',
  'PLAYER_REMOVED',
  'PLAYER_NOT_IN_SESSION',
  'AVATAR_TAKEN',
  'INVALID_TOKEN',
  'NOT_HOST',
  'FORBIDDEN',
  'INVALID_AMOUNT',
  'SELF_TRANSFER',
  'NOT_A_PARTICIPANT',
  'CONFLICTING_HAND_REPORT',
  'INVALID_SEAT',
  'RATE_LIMITED',
  'INTERNAL',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** Copy shown directly to players. Friendly, blame-free, never technical. §35 */
export const ERROR_MESSAGES: Record<ErrorCode, string> = {
  VALIDATION_FAILED: "That didn't look right. Please try again.",
  SESSION_NOT_FOUND: "We couldn't find that table.",
  SESSION_LOCKED: 'This table is locked. Ask the host to unlock it.',
  SESSION_ENDED: 'This game has already finished.',
  SESSION_FULL: 'This table is full.',
  ROUND_NOT_FOUND: "We couldn't find that hand.",
  ROUND_NOT_ACTIVE: 'This hand has already ended.',
  ROUND_ALREADY_ACTIVE: 'A hand is already in progress.',
  ROUND_ALREADY_COMPLETED: 'This hand has already ended.',
  ROUND_ALREADY_VOIDED: 'That hand has already been deleted.',
  NOT_ENOUGH_PLAYERS: 'At least 2 players are required.',
  PLAYER_NOT_FOUND: "We couldn't find that player.",
  PLAYER_REMOVED: 'You are no longer part of this table.',
  AVATAR_TAKEN: 'Somebody just took that one. Pick another.',
  PLAYER_NOT_IN_SESSION: 'That player is not at this table.',
  INVALID_TOKEN: "We couldn't confirm who you are. Try rejoining the table.",
  NOT_HOST: 'Only the host can do that.',
  FORBIDDEN: "You can't do that right now.",
  INVALID_AMOUNT: 'Enter a whole number of points above zero.',
  SELF_TRANSFER: "Points can't move to the same player.",
  NOT_A_PARTICIPANT: 'This hand started before you sat down. The next one is yours.',
  /*
    Two contradictory stories about one hand. Either one player paid everybody —
    which is the whole hand, and leaves nobody else with a loss to report — or
    each player reports their own loss and the silent one wins. A hand cannot be
    both, so the second kind of report is refused rather than averaged in. §3.2
  */
  CONFLICTING_HAND_REPORT:
    'A hand is either one player paying everybody or everyone reporting their own losses — not both.',
  INVALID_SEAT: "That seat isn't available.",
  RATE_LIMITED: "That was a lot at once. Give it a second and try again.",
  INTERNAL: "Couldn't record that point change. Reconnecting…",
};

/** Default HTTP status per code, for the REST surface. */
const HTTP_STATUS: Partial<Record<ErrorCode, number>> = {
  VALIDATION_FAILED: 400,
  INVALID_AMOUNT: 400,
  CONFLICTING_HAND_REPORT: 409,
  SELF_TRANSFER: 400,
  INVALID_SEAT: 400,
  INVALID_TOKEN: 401,
  NOT_HOST: 403,
  FORBIDDEN: 403,
  PLAYER_REMOVED: 403,
  NOT_A_PARTICIPANT: 403,
  SESSION_LOCKED: 403,
  SESSION_NOT_FOUND: 404,
  ROUND_NOT_FOUND: 404,
  PLAYER_NOT_FOUND: 404,
  PLAYER_NOT_IN_SESSION: 404,
  SESSION_ENDED: 409,
  SESSION_FULL: 409,
  AVATAR_TAKEN: 409,
  ROUND_NOT_ACTIVE: 409,
  ROUND_ALREADY_ACTIVE: 409,
  ROUND_ALREADY_COMPLETED: 409,
  ROUND_ALREADY_VOIDED: 409,
  NOT_ENOUGH_PLAYERS: 409,
  RATE_LIMITED: 429,
  INTERNAL: 500,
};

export function httpStatusForErrorCode(code: ErrorCode): number {
  return HTTP_STATUS[code] ?? 400;
}

export function messageForErrorCode(code: string): string {
  return (ERROR_MESSAGES as Record<string, string>)[code] ?? ERROR_MESSAGES.INTERNAL;
}

/** The only error shape that crosses the wire. */
export interface WireError {
  code: ErrorCode;
  message: string;
}

/**
 * Thrown by domain and service code. `message` is always safe to display.
 * `detail` stays server-side for logs.
 */
export class DomainError extends Error {
  readonly code: ErrorCode;
  readonly detail?: string;

  constructor(code: ErrorCode, detail?: string) {
    super(ERROR_MESSAGES[code]);
    this.name = 'DomainError';
    this.code = code;
    this.detail = detail;
  }

  get httpStatus(): number {
    return httpStatusForErrorCode(this.code);
  }

  toWire(): WireError {
    return { code: this.code, message: this.message };
  }
}

export function isDomainError(err: unknown): err is DomainError {
  return err instanceof DomainError;
}

/** Narrow any thrown value to a safe wire error. */
export function toWireError(err: unknown): WireError {
  if (isDomainError(err)) return err.toWire();
  return { code: 'INTERNAL', message: ERROR_MESSAGES.INTERNAL };
}
